import MomentUtils from "@date-io/moment";
import {
  CircularProgress,
  Grid,
  Switch,
  TextField,
} from "@material-ui/core";
import { LuSparkles } from "react-icons/lu";
import { Autocomplete } from "@material-ui/lab";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import React, { useState } from "react";
import CreateWithAIModal from "../Common/CreateWithAIModal";
import { resolveVesselPort, vesselPatchFromAI } from "../../util/aiImport/aiImportVessel";
import SplitHireModal from "../../../src/components/Calculator/SplitHireModal";
import { handleKeyDown } from "../../common/common";
import {
  fuelPriceOptions,
  scrubberOptions,
  vesselCalculatorMainFields,
} from "../../constants";

const VESSEL_AI_EXAMPLE = `MV Ocean Star, bulk carrier, IMO 9123456, MMSI 636019876, built 2012, flag Panama, class DNV.
DWT 56,000 MT, summer draft 12.8 m, LOA 190 m, beam 32.2 m, GRT 32,500, NRT 19,000, TPC 55, light ship 9,800.
Grain 70,000 cbm, bale 68,000 cbm. 4 x 30t cranes, 4 x 12 cbm grabs. Scrubber fitted.
Laden: 12 kn 24 mt, 13 kn 28 mt VLSFO, MGO 0.1. Ballast: 12.5 kn 23 mt, 13.5 kn 27 mt VLSFO, MGO 0.1.
Port mt/day: idle 4, loading 8, discharging 8, extra 2 VLSFO, MGO 0.5.
Open Singapore 25 Nov 2026. Hire USD 14,500/day, GBB USD 120,000. Extra expenses USD 5,000, constants 250 mt.
Split hire: 30 days at 14,500, then 20 days at 15,000.
Bunkers on delivery: 800 VLSFO, 50 MGO. Expected on redelivery: 700 VLSFO, 40 MGO, tolerance 50 VLSFO, 5 MGO.
Prices USD/MT: delivery 620 VLSFO 780 MGO, redelivery 630 VLSFO 790 MGO, settlement 625 VLSFO 785 MGO.`;

export function VesselAIModal({ aiOpen, setAiOpen, onAiFill }) {
  return (
    <CreateWithAIModal
      open={aiOpen}
      handleClose={() => setAiOpen(false)}
      kind="vessel"
      title="Create Vessel with AI"
      filledLabel="Vessel details filled"
      tips="Works best when it includes: vessel name, IMO, built year, DWT, dimensions, capacities, gear, and sea and port consumptions."
      example={VESSEL_AI_EXAMPLE}
      onParsed={async (result, onStatus, pastedText) => {
        const patch = vesselPatchFromAI(result);
        if (!Object.keys(patch).length) throw new Error("No vessel details found in the text.");
        let warning = "";
        if (result?.openPort) {
          onStatus("Finding open port...");
          const port = await resolveVesselPort(result.openPort);
          if (port) Object.assign(patch, { port: port.name, selectedPort: port, lat: port.lat || "", lng: port.lng || "" });
          else warning = `Open port "${result.openPort}" not found. Please select it manually.`;
        }
        patch.vesselTextDescription = pastedText;
        onAiFill(patch);
        return warning;
      }}
    />
  );
}

function AddNewVessel(props) {
  const {
    onChangeHandler = () => { },
    onChangeSplitHire = () => { },
    onChangeBunkerPriceOverridden = () => { },
    addNewSplitHire = () => { },
    vessel = {},
    showContractLabels = false,
    contractFields = null,
    onChangePort = () => { },
    savePortList,
    portLoading = false,
    delayPortSearch,
    onAiFill = null,
    aiOpen = false,
    setAiOpen = () => { },
  } = props;

  const {
    vesselBalConsmHS1 = 0,
    vesselBalConsmHS2 = 0,
    vesselBalConsmHS3 = 0,
    vesselBalConsmHS4 = 0,
    vesselBalConsmLS1 = 0,
    vesselBalConsmLS2 = 0,
    vesselBalConsmLS3 = 0,
    vesselBalConsmLS4 = 0,
    vesselBalConsmMGO1 = 0,
    vesselBalConsmMGO2 = 0,
    vesselBalConsmMGO3 = 0,
    vesselBalConsmMGO4 = 0,
    vesselBalSpd1 = 0,
    vesselBalSpd2 = 0,
    vesselBalSpd3 = 0,
    vesselBalSpd4 = 0,
    vesselDwt = 0,
    vesselName = "",
    vesselLdnConsmHS1 = 0,
    vesselLdnConsmHS2 = 0,
    vesselLdnConsmHS3 = 0,
    vesselLdnConsmHS4 = 0,
    vesselLdnConsmLS1 = 0,
    vesselLdnConsmLS2 = 0,
    vesselLdnConsmLS3 = 0,
    vesselLdnConsmLS4 = 0,
    vesselLdnConsmMGO1 = 0,
    vesselLdnConsmMGO2 = 0,
    vesselLdnConsmMGO3 = 0,
    vesselLdnConsmMGO4 = 0,
    vesselLdnSpd1 = 0,
    vesselLdnSpd2 = 0,
    vesselLdnSpd3 = 0,
    vesselLdnSpd4 = 0,
    vesselPDischConsmHS = 0,
    vesselPDischConsmLS = 0,
    vesselPDischConsmMGO = 0,
    vesselPIdleConsmHS = 0,
    vesselPIdleConsmLS = 0,
    vesselPIdleConsmMGO = 0,
    vesselPLdgConsmHS = 0,
    vesselPLdgConsmLS = 0,
    vesselPLdgConsmMGO = 0,
    vesselVExtraConsmHS = 0,
    vesselVExtraConsmLS = 0,
    vesselVExtraConsmMGO = 0,
    vesselIMO = "",
    fuelType = "",
    vesselBalComb1Selected,
    vesselBalComb2Selected,
    vesselBalComb3Selected,
    vesselBalComb4Selected,
    vesselLadComb1Selected,
    vesselLadComb2Selected,
    vesselLadComb3Selected,
    vesselLadComb4Selected,
    vesselScrubber,
    bnkDelhs = 0,
    bnkDells = 0,
    bnkDelmgo = 0,
    bnkPriceDeliveryhs = 0,
    bnkPriceDeliveryls = 0,
    bnkPriceDeliverymgo = 0,
    bnkPriceRedeliveryhs = 0,
    bnkPriceRedeliveryls = 0,
    bnkPriceRedeliverymgo = 0,
    bnkPriceSettlemenths = 0,
    bnkPriceSettlementls = 0,
    bnkPriceSettlementmgo = 0,
    bnkQtyRedeliveryhs = 0,
    bnkQtyRedeliveryls = 0,
    bnkQtyRedeliverymgo = 0,
    bnkRedeliveryQtyTolerancehs = 0,
    bnkRedeliveryQtyTolerancels = 0,
    bnkRedeliveryQtyTolerancemgo = 0,
    bunkerPriceOverridden = false,
    vesselTextDescription = "",
  } = vessel || {};

  return (
    <Grid container className="w-full">
      {/* Left 8 Columns on desktop: Vessel Details & Consumptions & Bunker */}
      <Grid item xs={12} lg={8} className="lg:pr-3 mb-3 lg:mb-0">
        <Grid container>
          {/* Top Section: Vessel Details & Sea/Port Consumptions */}
          <Grid item xs={12} className="border-b border-slate-300 pb-2 mb-2">
            <div className="flex items-center gap-3 w-full mb-1.5">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap">Vessel</span>
                <input
                  value={vesselName}
                  name="vesselName"
                  type="text"
                  className="h-[20px] px-1.5 text-[11px] font-medium bg-[#ffffd6] border border-slate-300 w-full focus:bg-white focus:outline focus:outline-[1.5px] focus:outline-[#d97706] focus:-outline-offset-[1.5px]"
                  onChange={onChangeHandler}
                />
              </div>
              <div className="flex items-center gap-1.5 w-[85px] shrink-0">
                <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap">Dwt</span>
                <input
                  value={vesselDwt}
                  name="vesselDwt"
                  type="number"
                  onKeyDown={handleKeyDown}
                  className="h-[20px] px-1.5 text-[11px] font-medium bg-[#ffffd6] border border-slate-300 w-full focus:bg-white focus:outline focus:outline-[1.5px] focus:outline-[#d97706] focus:-outline-offset-[1.5px]"
                  onChange={onChangeHandler}
                />
              </div>
              <div className="flex items-center gap-1.5 w-[85px] shrink-0">
                <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap">IMO</span>
                <input
                  value={vesselIMO}
                  name="vesselIMO"
                  type="text"
                  className="h-[20px] px-1.5 text-[11px] font-medium bg-[#ffffd6] border border-slate-300 w-full focus:bg-white focus:outline focus:outline-[1.5px] focus:outline-[#d97706] focus:-outline-offset-[1.5px]"
                  onChange={onChangeHandler}
                />
              </div>
            </div>

            <Grid container>
              {/* Left Column: Vessel Details Key-Value Table */}
              <Grid item xs={12} sm={5} className="sm:pr-1.5 mb-2 sm:mb-0">
                <table className="fleet-result-table vesselDetails">
                  <tbody>
                    {vesselCalculatorMainFields.map((item, key) => {
                      if (item.name === "fuelType") {
                        return (
                          <tr key={key}>
                            <th>{item.label}</th>
                            <td>
                              <select
                                value={fuelType || "Type-1"}
                                name="fuelType"
                                className="h-[19.5px] text-[11px] bg-[#ffffd6] border-0 outline-none w-full cursor-pointer px-1"
                                onChange={onChangeHandler}
                              >
                                {fuelPriceOptions.map((opt, index) => (
                                  <option key={index} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      }
                      if (item.name === "vesselScrubber") {
                        return (
                          <tr key={key}>
                            <th>{item.label}</th>
                            <td>
                              <select
                                value={vesselScrubber || "Yes"}
                                name="vesselScrubber"
                                className="h-[19.5px] text-[11px] bg-[#ffffd6] border-0 outline-none w-full cursor-pointer px-1"
                                onChange={onChangeHandler}
                              >
                                {scrubberOptions.map((opt, index) => (
                                  <option key={index} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={key}>
                          <th>{item.label}</th>
                          <td>
                            <input
                              value={vessel[item.name] ?? (item.type === "number" ? 0 : "")}
                              type={item.type}
                              onKeyDown={handleKeyDown}
                              name={item.name}
                              onChange={onChangeHandler}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Grid>

              {/* Right Column: Consumptions Tables */}
              <Grid item xs={12} sm={7} className="sm:pl-1.5">
                <div className="text-[11px] font-medium text-slate-800 mt-1 mb-1 leading-tight">
                  Ballast Sea Consumptions
                </div>
                <table className="vessel-details-tables w-full mb-2" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "8%", textAlign: "center" }}>✓</th>
                      <th style={{ width: "23%" }}>Speed</th>
                      <th style={{ width: "23%" }}>Main</th>
                      <th style={{ width: "23%" }}>ECA</th>
                      <th style={{ width: "23%" }}>MGO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselBalComb1Selected"
                          checked={vesselBalComb1Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalSpd1"
                          value={vesselBalSpd1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmHS1"
                          value={vesselBalConsmHS1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmLS1"
                          value={vesselBalConsmLS1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmMGO1"
                          value={vesselBalConsmMGO1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselBalComb2Selected"
                          checked={vesselBalComb2Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalSpd2"
                          value={vesselBalSpd2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmHS2"
                          value={vesselBalConsmHS2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmLS2"
                          value={vesselBalConsmLS2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmMGO2"
                          value={vesselBalConsmMGO2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselBalComb3Selected"
                          checked={vesselBalComb3Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalSpd3"
                          value={vesselBalSpd3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmHS3"
                          value={vesselBalConsmHS3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmLS3"
                          value={vesselBalConsmLS3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmMGO3"
                          value={vesselBalConsmMGO3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselBalComb4Selected"
                          checked={vesselBalComb4Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalSpd4"
                          value={vesselBalSpd4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmHS4"
                          value={vesselBalConsmHS4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmLS4"
                          value={vesselBalConsmLS4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselBalConsmMGO4"
                          value={vesselBalConsmMGO4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="text-[11px] font-medium text-slate-800 mt-3 mb-1 leading-tight">
                  Laden Sea Consumptions
                </div>
                <table className="vessel-details-tables w-full mb-2" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "8%", textAlign: "center" }}>✓</th>
                      <th style={{ width: "23%" }}>Speed</th>
                      <th style={{ width: "23%" }}>Main</th>
                      <th style={{ width: "23%" }}>ECA</th>
                      <th style={{ width: "23%" }}>MGO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselLadComb1Selected"
                          checked={vesselLadComb1Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnSpd1"
                          value={vesselLdnSpd1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmHS1"
                          value={vesselLdnConsmHS1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmLS1"
                          value={vesselLdnConsmLS1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmMGO1"
                          value={vesselLdnConsmMGO1 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselLadComb2Selected"
                          checked={vesselLadComb2Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnSpd2"
                          value={vesselLdnSpd2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmHS2"
                          value={vesselLdnConsmHS2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmLS2"
                          value={vesselLdnConsmLS2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmMGO2"
                          value={vesselLdnConsmMGO2 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselLadComb3Selected"
                          checked={vesselLadComb3Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnSpd3"
                          value={vesselLdnSpd3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmHS3"
                          value={vesselLdnConsmHS3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmLS3"
                          value={vesselLdnConsmLS3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmMGO3"
                          value={vesselLdnConsmMGO3 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          name="vesselLadComb4Selected"
                          checked={vesselLadComb4Selected || false}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnSpd4"
                          value={vesselLdnSpd4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmHS4"
                          value={vesselLdnConsmHS4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmLS4"
                          value={vesselLdnConsmLS4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselLdnConsmMGO4"
                          value={vesselLdnConsmMGO4 || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="text-[11px] font-medium text-slate-800 mt-3 mb-1 leading-tight">
                  Port Consumptions
                </div>
                <table className="vessel-details-tables w-full" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "37%" }}></th>
                      <th style={{ width: "21%" }}>Main</th>
                      <th style={{ width: "21%" }}>ECA</th>
                      <th style={{ width: "21%" }}>MGO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>Idle</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPIdleConsmHS"
                          value={vesselPIdleConsmHS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPIdleConsmLS"
                          value={vesselPIdleConsmLS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPIdleConsmMGO"
                          value={vesselPIdleConsmMGO || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Loading</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPLdgConsmHS"
                          value={vesselPLdgConsmHS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPLdgConsmLS"
                          value={vesselPLdgConsmLS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPLdgConsmMGO"
                          value={vesselPLdgConsmMGO || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Disch</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPDischConsmHS"
                          value={vesselPDischConsmHS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPDischConsmLS"
                          value={vesselPDischConsmLS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselPDischConsmMGO"
                          value={vesselPDischConsmMGO || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Extra</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselVExtraConsmHS"
                          value={vesselVExtraConsmHS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselVExtraConsmLS"
                          value={vesselVExtraConsmLS || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          placeholder="0"
                          name="vesselVExtraConsmMGO"
                          value={vesselVExtraConsmMGO || ""}
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
            </Grid>
          </Grid>

          {/* Bottom Section: Split Hire, Expenses, Port, Bunker */}
          <Grid item xs={12}>
            <Grid container>
              {/* Bottom Left: Split Hire & Extra Expenses */}
              <Grid item xs={12} sm={6} className="sm:pr-1.5 mb-2 sm:mb-0">
                <div className="text-[12px] font-medium text-slate-700 mb-1 leading-tight">
                  Split hire
                </div>
                <SplitHireModal
                  addNewSplitHire={addNewSplitHire}
                  selectedSplitHire={vessel}
                  onChangeSplitHire={onChangeSplitHire}
                />
                <div className="mt-2.5">
                  <table className="fleet-result-table w-full" style={showContractLabels ? { tableLayout: "fixed" } : undefined}>
                    {showContractLabels && <colgroup><col style={{ width: "33%" }} /><col style={{ width: "17%" }} /><col style={{ width: "33%" }} /><col style={{ width: "17%" }} /></colgroup>}
                    <tbody>
                      <tr>
                        <th style={{ width: showContractLabels ? "33%" : "35%" }}>{showContractLabels ? "Vessel Extra Expense" : "Extra Expenses"}</th>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            placeholder="0"
                            onChange={onChangeHandler}
                            value={(vessel && vessel.extraExpense) || 0}
                            name="extraExpense"
                          />
                        </td>
                        {showContractLabels && (
                          <>
                            <th style={{ width: "33%" }}>Constant</th>
                            <td>
                              <input type="number" onKeyDown={handleKeyDown} placeholder="0" onChange={onChangeHandler} value={(vessel && vessel.constants) || 0} name="constants" />
                            </td>
                          </>
                        )}
                      </tr>
                      {!showContractLabels && (
                        <tr>
                          <th>Constants</th>
                          <td>
                            <input type="number" onKeyDown={handleKeyDown} placeholder="0" onChange={onChangeHandler} value={(vessel && vessel.constants) || 0} name="constants" />
                          </td>
                        </tr>
                      )}
                      {contractFields}
                    </tbody>
                  </table>
                </div>
              </Grid>

              {/* Bottom Right: Port, Hire, Bunker Qty & Prices */}
              <Grid item xs={12} sm={6} className="sm:pl-1.5">
                <table className="fleet-result-table w-full">
                  <tbody>
                    <tr>
                      <th style={{ width: "35%" }}>Port</th>
                      <td>
                        <Autocomplete
                          options={savePortList || []}
                          value={vessel?.selectedPort || null}
                          getOptionLabel={(option) => option?.portLabel || ""}
                          style={{ width: "100%" }}
                          disableClearable
                          onChange={(e, value) => onChangePort("port", value)}
                          className="w-full autocomplete-mui bg-[#ffffd6]"
                          classes={{ paper: "compact-popover" }}
                          getOptionSelected={(option, value) => option.portLabel === value}
                          blurOnSelect
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Search Port"
                              onChange={(e) => delayPortSearch(e.target.value)}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {portLoading[0] && (
                                      <CircularProgress color="inherit" size={12} />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              }}
                            />
                          )}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Date Opening</th>
                      <td>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <DateTimePicker
                            inputVariant="outlined"
                            size="small"
                            name="dateOpen"
                            className="date-time-picker-update-fleet cursor-pointer"
                            style={{ width: "100%", background: "#ffffd6", cursor: "pointer" }}
                            value={(vessel && vessel.dateOpen) || Date.now()}
                            onChange={onChangeHandler}
                            format="MMM DD, YYYY HH:mm"
                            ampm={false}
                          />
                        </MuiPickersUtilsProvider>
                      </td>
                    </tr>
                    <tr>
                      <th>Hire Per Day</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          value={(vessel && vessel.hirePerDay) || 0}
                          name="hirePerDay"
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Hire GBB</th>
                      <td>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          value={(vessel && vessel.hireGbb) || 0}
                          name="hireGbb"
                          onChange={onChangeHandler}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Bunker Qty */}
                <div className="mt-2.5">
                  <table className="fleet-result-table w-full" style={{ tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ width: "37%" }}>Bunker Qty</th>
                        <th style={{ width: "21%" }}>Main</th>
                        <th style={{ width: "21%" }}>ECA</th>
                        <th style={{ width: "21%" }}>MGO</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>On Delivery</th>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkDelhs}
                            name="bnkDelhs"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkDells}
                            name="bnkDells"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkDelmgo}
                            name="bnkDelmgo"
                            onChange={onChangeHandler}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>Exp on Re-Delivery</th>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkQtyRedeliveryhs}
                            name="bnkQtyRedeliveryhs"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkQtyRedeliveryls}
                            name="bnkQtyRedeliveryls"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkQtyRedeliverymgo}
                            name="bnkQtyRedeliverymgo"
                            onChange={onChangeHandler}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>Tolerance Acceptable</th>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkRedeliveryQtyTolerancehs}
                            name="bnkRedeliveryQtyTolerancehs"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkRedeliveryQtyTolerancels}
                            name="bnkRedeliveryQtyTolerancels"
                            onChange={onChangeHandler}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkRedeliveryQtyTolerancemgo}
                            name="bnkRedeliveryQtyTolerancemgo"
                            onChange={onChangeHandler}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Override Switch */}
                <div className="sync-bunker-switch flex justify-end items-center my-1">
                  <label>Override</label>
                  <Switch
                    checked={!!bunkerPriceOverridden}
                    onChange={(e) => onChangeBunkerPriceOverridden(e)}
                    name="bunkerPriceOverridden"
                    color="primary"
                  />
                </div>

                {/* Bunker Prices */}
                <table className="fleet-result-table w-full" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "37%" }}>Bunker Prices</th>
                      <th style={{ width: "21%" }}>Main</th>
                      <th style={{ width: "21%" }}>ECA</th>
                      <th style={{ width: "21%" }}>MGO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>On Delivery</th>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceDeliveryhs}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceDeliveryhs}
                            name="bnkPriceDeliveryhs"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceDeliveryls}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceDeliveryls}
                            name="bnkPriceDeliveryls"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceDeliverymgo}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceDeliverymgo}
                            name="bnkPriceDeliverymgo"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>On Re-Delivery</th>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceRedeliveryhs}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceRedeliveryhs}
                            name="bnkPriceRedeliveryhs"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceRedeliveryls}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceRedeliveryls}
                            name="bnkPriceRedeliveryls"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceRedeliverymgo}</p>
                        ) : (
                          <input
                            onKeyDown={handleKeyDown}
                            type="number"
                            value={bnkPriceRedeliverymgo}
                            name="bnkPriceRedeliverymgo"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>For Settlement</th>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceSettlemenths}</p>
                        ) : (
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkPriceSettlemenths}
                            name="bnkPriceSettlemenths"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceSettlementls}</p>
                        ) : (
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkPriceSettlementls}
                            name="bnkPriceSettlementls"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                      <td>
                        {!bunkerPriceOverridden ? (
                          <p>{bnkPriceSettlementmgo}</p>
                        ) : (
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={bnkPriceSettlementmgo}
                            name="bnkPriceSettlementmgo"
                            onChange={onChangeHandler}
                          />
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Right 4 Columns on desktop: Vessel Text Description */}
      <Grid item xs={12} lg={4} className="lg:pl-3 flex flex-col mt-3 lg:mt-0">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="text-[12px] font-semibold text-slate-700 tracking-tight">
            Vessel Text Description
          </div>
        </div>
        <textarea
          value={vesselTextDescription || vessel?.vesselExtraDetails?.vesselTextDescription || vessel?.remark || ""}
          name="vesselTextDescription"
          onChange={onChangeHandler}
          className="w-full flex-1 min-h-[250px] lg:min-h-[480px] text-[11px] font-mono p-3 bg-white border border-slate-300 rounded-none focus:border-[#313C4C] focus:outline-none placeholder:text-slate-400 resize-none transition-colors"
          placeholder="Paste or enter vessel description here..."
        />
        {onAiFill && (
          <VesselAIModal aiOpen={aiOpen} setAiOpen={setAiOpen} onAiFill={onAiFill} />
        )}
      </Grid>
    </Grid>
  );
}

export default AddNewVessel;
